import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const rp = (n) => `Rp ${Math.round(n || 0).toLocaleString("id-ID")}`;
const short = (n) => (Math.abs(n) >= 1e6 ? `${(n / 1e6).toFixed(1)}jt` : `${Math.round(n / 1e3)}rb`);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const label = (ym) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;

export function MarginReport() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/margins").then((r) => setD(r.data)).catch(() => setD({ rows: [], months: [] })); }, []);
  if (!d) return null;
  const cur = d.months[d.months.length - 1];
  return (
    <div className="border border-border bg-card rounded-sm" data-testid="margin-report">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div className="overline text-muted-foreground">Margin Keuntungan per Penawaran</div>
        {cur && <span className="text-xs text-muted-foreground" data-testid="margin-current-month">{label(cur.month)}: deal <b className="text-emerald-600">{rp(cur.won_margin)}</b> · pipeline {rp(cur.pipeline_margin)}</span>}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 p-6">
        <div className="lg:col-span-2 h-64" data-testid="margin-chart">
          {d.months.length === 0 ? <div className="text-sm text-muted-foreground">Belum ada data penawaran.</div> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.months.map((m) => ({ ...m, name: label(m.month) }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" fontSize={11} />
                <YAxis tickFormatter={short} fontSize={11} width={48} />
                <Tooltip formatter={(v) => rp(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="won_margin" name="Margin deal" fill="#10b981" radius={[2, 2, 0, 0]} />
                <Bar dataKey="pipeline_margin" name="Margin semua penawaran" fill="#F0592B" opacity={0.45} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="lg:col-span-3 overflow-x-auto max-h-64 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground sticky top-0 bg-card"><tr>
              {["No. Ref", "Customer", "Modal", "Harga Jual", "Margin", "%"].map((h) => <th key={h} className="text-left py-1.5 pr-3 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {d.rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground" data-testid="margin-empty">Belum ada penawaran.</td></tr>}
              {d.rows.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid="margin-row">
                  <td className="py-1.5 pr-3 font-mono"><Link to={`/quotations/${r.id}`} className="hover:underline">{r.quote_number}</Link></td>
                  <td className="py-1.5 pr-3 truncate max-w-[160px]">{r.customer}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{rp(r.cost)}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{rp(r.subtotal)}</td>
                  <td className={`py-1.5 pr-3 font-mono text-xs ${r.won ? "text-emerald-600 font-semibold" : ""}`}>{rp(r.margin)}</td>
                  <td className={`py-1.5 pr-3 text-xs ${r.margin_percent !== null && r.margin_percent < 10 ? "text-red-600 font-semibold" : ""}`}>{r.margin_percent === null ? "–" : `${r.margin_percent}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
